import React, { ReactNode, useEffect, useRef, useState, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  draggable?: boolean;
  resizable?: boolean;
  headerActions?: ReactNode;
  modalClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  testId?: string;
}

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[95vw] w-full',
};

// Export context and hook so children can query and respond to the modal width container
export const ModalWidthContext = createContext<string>('modal-w-lg');
export const useModalWidth = () => useContext(ModalWidthContext);

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'lg',
  draggable = true,
  resizable = true,
  headerActions,
  modalClassName = '',
  headerClassName = '',
  titleClassName = '',
  testId,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  
  // Position state (Dragging)
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const positionRef = useRef({ x: 0, y: 0 });

  // Maximized / Size scaling state
  const [isMaximized, setIsMaximized] = useState(false);
  const [dimensions, setDimensions] = useState<{ width?: number; height?: number }>({});
  
  // Resizing state
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef({ width: 0, height: 0, x: 0, y: 0 });

  // Active width category for container-responsive child layouts
  const [activeWidthClass, setActiveWidthClass] = useState<string>('modal-w-lg');

  // Reset position & dimensions when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setPosition({ x: 0, y: 0 });
      positionRef.current = { x: 0, y: 0 };
      setIsMaximized(false);
      setDimensions({});
      setIsResizing(false);
      setActiveWidthClass('modal-w-lg');
    }
  }, [isOpen]);

  // Handle body scrolling
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // ResizeObserver to track container-relative width breakpoints
  useEffect(() => {
    if (!isOpen) return;
    const element = modalRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width < 480) {
          setActiveWidthClass('modal-w-xs');
        } else if (width < 640) {
          setActiveWidthClass('modal-w-sm');
        } else if (width < 768) {
          setActiveWidthClass('modal-w-md');
        } else if (width < 1024) {
          setActiveWidthClass('modal-w-lg');
        } else {
          setActiveWidthClass('modal-w-xl');
        }
      }
    });

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [isOpen]);

  // Dragging logic (only enabled if not maximized)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!draggable || isMaximized) return;
    
    // Do not trigger dragging on button click or interaction inside header
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('input')) {
      return;
    }

    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    positionRef.current = position;
  }, [draggable, isMaximized, position]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setPosition({
      x: positionRef.current.x + dx,
      y: positionRef.current.y + dy,
    });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Resizing logic
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    if (!resizable || isMaximized) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    
    const rect = modalRef.current?.getBoundingClientRect();
    if (rect) {
      resizeStartRef.current = {
        width: rect.width,
        height: rect.height,
        x: e.clientX,
        y: e.clientY
      };
    }
  }, [resizable, isMaximized]);

  const handleResizeMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    const deltaX = e.clientX - resizeStartRef.current.x;
    const deltaY = e.clientY - resizeStartRef.current.y;
    
    // Bounds constraints
    const minWidth = 400;
    const minHeight = 280;
    const maxWidth = window.innerWidth - 32;
    const maxHeight = window.innerHeight - 32;

    const newWidth = Math.max(minWidth, Math.min(resizeStartRef.current.width + deltaX, maxWidth));
    const newHeight = Math.max(minHeight, Math.min(resizeStartRef.current.height + deltaY, maxHeight));

    setDimensions({
      width: newWidth,
      height: newHeight
    });
  }, [isResizing]);

  const handleResizeMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMouseMove);
      document.addEventListener('mouseup', handleResizeMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleResizeMouseMove);
        document.removeEventListener('mouseup', handleResizeMouseUp);
      };
    }
  }, [isResizing, handleResizeMouseMove, handleResizeMouseUp]);

  // Toggle maximize
  const handleToggleMaximize = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMaximized(prev => !prev);
    // Reset dragging offset on scale up to preserve focus containment
    setPosition({ x: 0, y: 0 });
    positionRef.current = { x: 0, y: 0 };
  }, []);

  // Focus trap and keyboard handling
  useEffect(() => {
    if (!isOpen) return;

    const modalElement = modalRef.current;
    if (!modalElement) return;

    const focusableElements = modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
      handleTabKey(event);
    };

    window.addEventListener('keydown', handleKeyDown);

    const activeElement = document.activeElement;
    const isAlreadyFocusedInModal = activeElement && modalElement.contains(activeElement);
    if (!isAlreadyFocusedInModal && firstElement) {
      requestAnimationFrame(() => {
        firstElement.focus();
      });
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="modal-title"
      data-testid={testId}
    >
      <style dangerouslySetInnerHTML={{ __html: `
        .modal-scale-wrapper {
          --modal-scale: 1.0;
        }
        .modal-scale-wrapper.modal-w-xs {
          --modal-scale: 0.80;
        }
        .modal-scale-wrapper.modal-w-sm {
          --modal-scale: 0.90;
        }
        .modal-scale-wrapper.modal-w-md {
          --modal-scale: 0.95;
        }
        .modal-scale-wrapper.modal-w-lg {
          --modal-scale: 1.0;
        }
        .modal-scale-wrapper.modal-w-xl {
          --modal-scale: 1.10;
        }

        /* Responsive typography scaling */
        .modal-scale-wrapper h1,
        .modal-scale-wrapper .text-3xl {
          font-size: calc(1.875rem * var(--modal-scale)) !important;
        }
        .modal-scale-wrapper h2,
        .modal-scale-wrapper .text-2xl {
          font-size: calc(1.5rem * var(--modal-scale)) !important;
        }
        .modal-scale-wrapper h3,
        .modal-scale-wrapper .text-xl {
          font-size: calc(1.25rem * var(--modal-scale)) !important;
        }
        .modal-scale-wrapper h4,
        .modal-scale-wrapper .text-lg {
          font-size: calc(1.125rem * var(--modal-scale)) !important;
        }
        .modal-scale-wrapper .text-base {
          font-size: calc(1rem * var(--modal-scale)) !important;
        }
        .modal-scale-wrapper p,
        .modal-scale-wrapper .text-sm {
          font-size: calc(0.875rem * var(--modal-scale)) !important;
        }
        .modal-scale-wrapper span:not(.no-scale),
        .modal-scale-wrapper .text-xs {
          font-size: calc(0.75rem * var(--modal-scale)) !important;
        }
        .modal-scale-wrapper .text-\\[10px\\] {
          font-size: calc(0.625rem * var(--modal-scale)) !important;
        }

        /* Responsive spacing scaling */
        .modal-scale-wrapper .p-6 {
          padding: calc(1.5rem * var(--modal-scale)) !important;
        }
        .modal-scale-wrapper .p-5 {
          padding: calc(1.25rem * var(--modal-scale)) !important;
        }
        .modal-scale-wrapper .p-4 {
          padding: calc(1rem * var(--modal-scale)) !important;
        }
        .modal-scale-wrapper .gap-4 {
          gap: calc(1rem * var(--modal-scale)) !important;
        }
        .modal-scale-wrapper .gap-3 {
          gap: calc(0.75rem * var(--modal-scale)) !important;
        }
        
        /* Table responsive adjustments */
        .modal-scale-wrapper table th,
        .modal-scale-wrapper table td {
          font-size: calc(0.875rem * var(--modal-scale)) !important;
          padding-top: calc(0.75rem * var(--modal-scale)) !important;
          padding-bottom: calc(0.75rem * var(--modal-scale)) !important;
          padding-left: calc(1rem * var(--modal-scale)) !important;
          padding-right: calc(1rem * var(--modal-scale)) !important;
        }

        /* Button responsive scaling */
        .modal-scale-wrapper button:not(.no-scale) {
          font-size: calc(0.875rem * var(--modal-scale)) !important;
          padding-top: calc(0.625rem * var(--modal-scale)) !important;
          padding-bottom: calc(0.625rem * var(--modal-scale)) !important;
          padding-left: calc(1.25rem * var(--modal-scale)) !important;
          padding-right: calc(1.25rem * var(--modal-scale)) !important;
        }
      `}} />

      {/* Backdrop with blur effect */}
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div
        ref={modalRef}
        style={{
          transform: isMaximized ? 'none' : `translate(${position.x}px, ${position.y}px)`,
          transition: isDragging || isResizing ? 'none' : 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), width 0.2s cubic-bezier(0.4, 0, 0.2, 1), height 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          width: isMaximized ? '95vw' : (dimensions.width ? `${dimensions.width}px` : undefined),
          height: isMaximized ? '90vh' : (dimensions.height ? `${dimensions.height}px` : undefined),
        }}
        className={`
          relative bg-white dark:bg-dark-bgSecondary rounded-xl shadow-2xl 
          w-full flex flex-col overflow-hidden
          ring-1 ring-black/5 dark:ring-white/10
          animate-modal-enter
          ${activeWidthClass}
          modal-scale-wrapper
          ${isMaximized ? 'max-w-none max-h-none' : dimensions.width ? 'max-w-none' : SIZE_CLASSES[size]}
          ${isMaximized ? '' : dimensions.height ? 'max-h-none' : 'max-h-[90vh]'}
          ${modalClassName}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Draggable Header */}
        <div
          ref={headerRef}
          onMouseDown={handleMouseDown}
          className={`
            flex justify-between items-center px-5 py-4
            border-b border-neutral-200 dark:border-dark-border
            bg-gradient-to-r from-neutral-50 to-white dark:from-dark-bgAccent dark:to-dark-bgSecondary
            ${draggable && !isMaximized ? 'cursor-move select-none' : ''}
            ${headerClassName}
          `}
        >
          <h3
            id="modal-title"
            className={`text-lg font-semibold text-theme-text-primary no-scale ${titleClassName}`}
          >
            {title}
          </h3>
          
          <div className="flex items-center gap-2 relative z-50">
            {headerActions}
            
            {/* Maximize / Restore Control */}
            {resizable && (
              <button
                type="button"
                onClick={handleToggleMaximize}
                className="
                  p-1.5 rounded-lg text-neutral-400 
                  hover:text-neutral-600 hover:bg-neutral-100
                  dark:hover:text-white dark:hover:bg-dark-border
                  transition-all duration-200
                  no-scale
                "
                aria-label={isMaximized ? "Restore modal size" : "Maximize modal"}
              >
                {isMaximized ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 no-scale" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 14h6v6m10-6h-6v6M4 10h6V4m10 6h-6V4" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 no-scale" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
                  </svg>
                )}
              </button>
            )}

            {/* Close Control */}
            <button
              type="button"
              onClick={onClose}
              className="
                p-1.5 rounded-lg text-neutral-400 
                hover:text-neutral-600 hover:bg-neutral-100
                dark:hover:text-white dark:hover:bg-dark-border
                transition-all duration-200
                no-scale
              "
              aria-label="Close modal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 no-scale"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="px-5 py-4 border-t border-neutral-200 dark:border-dark-border bg-neutral-50/50 dark:bg-dark-bgAccent/50">
            {footer}
          </div>
        )}

        {/* Resizing Handle (Bottom-Right) */}
        {resizable && !isMaximized && (
          <div
            onMouseDown={handleResizeMouseDown}
            className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize flex items-end justify-end p-0.5 z-50 select-none group no-scale"
            title="Drag to resize"
          >
            <svg
              className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500 hover:text-theme-primary transition-colors duration-200 no-scale"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" d="M19 19L5 5M19 13L13 19M19 7L7 19" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(
    <ModalWidthContext.Provider value={activeWidthClass}>
      {modalContent}
    </ModalWidthContext.Provider>,
    document.body
  );
};

export default Modal;
